import {
  resolveReportCalloutTitle,
} from '@act/shared/data-transfer-objects';
import { LightbulbIcon } from '@phosphor-icons/react';

const midnight = 'var(--reema-midnight-100)';
const coral = 'var(--reema-coral-100)';

/** Default placeholder when the builder callout field is empty. */
export const REPORT_CALLOUT_PLACEHOLDER =
  'Add supporting context, a quote, or a highlight for this section.';

export interface ReportCalloutProps {
  /** Author-edited callout body. */
  value: string | null;
  /** Optional custom callout heading; null uses {@link REPORT_CALLOUT_DEFAULT_TITLE}. */
  title?: string | null;
  /** Builder preview: edit callout inline. */
  editable?: boolean;
  /** @param value - Updated callout text after inline edit. */
  onChange?: (value: string) => void;
  /** @param value - Updated callout title after inline edit. */
  onTitleChange?: (value: string) => void;
  /** Placeholder copy when `value` is empty in edit mode. */
  placeholder?: string;
  /** Optional classes on the outer callout card. */
  className?: string;
}

/**
 * Studio callout card shared across report sections (peach panel, lightbulb label).
 * @param props - Callout text and optional inline authoring handlers.
 */
export const ReportCallout = ({
  value,
  title = null,
  editable = false,
  onChange,
  onTitleChange,
  placeholder = REPORT_CALLOUT_PLACEHOLDER,
  className = '',
}: ReportCalloutProps) => {
  const displayTitle = resolveReportCalloutTitle(title);

  return (
    <aside
      className={`rounded-2xl p-6 text-left ${className}`.trim()}
      style={{
        background: 'rgba(255,237,218,0.92)',
        border: '1px solid rgba(255,123,101,0.2)',
      }}
    >
      <p
        className="mb-3 flex items-center gap-2 text-sm font-semibold"
        style={{ color: midnight, fontFamily: 'var(--reema-font-body)' }}
      >
        <LightbulbIcon
          className="h-4 w-4 shrink-0"
          style={{ color: coral }}
          aria-hidden
        />
        <span
          className="outline-none"
          contentEditable={editable}
          suppressContentEditableWarning={editable}
          onBlur={
            editable && onTitleChange
              ? event => onTitleChange(event.currentTarget.textContent ?? '')
              : undefined
          }
        >
          {displayTitle}
        </span>
      </p>
      <p
        className="whitespace-pre-wrap text-[15px] leading-relaxed"
        style={{ color: midnight, fontFamily: 'var(--reema-font-body)' }}
        contentEditable={editable}
        suppressContentEditableWarning={editable}
        onBlur={
          editable && onChange
            ? event => onChange((event.currentTarget.textContent ?? '').trim())
            : undefined
        }
      >
        {value ?? (editable ? placeholder : '')}
      </p>
    </aside>
  );
};
